"use client";

import { useEffect, useRef, useState } from "react";

import { Icona } from "@/components/home/Icona";
import { FUOCO } from "@/components/ui/pulsanti";
import { articleHref } from "@/lib/slug";
import type { NewsItem } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Le cinque cose che servono per condividere un pezzo.
 *
 *  Non è un `NewsItem` di proposito: la pagina di lettura degli
 *  editoriali provvisori (`/editoriali/{id}/{slug}`) non ha un
 *  dispaccio dietro, ma si condivide esattamente allo stesso modo.
 *  Ridurre la barra a questa forma evita la seconda copia della logica
 *  degli appunti, che è la cosa che poi diverge, come è già successo
 *  con le tabelle delle priorità. */
export type ShareTarget = {
  /** Il codice del pezzo, in coda al testo copiato. */
  id: string;
  /** L'indirizzo interno, senza origine.
   *
   *  L'origine del sito non è scritta da nessuna parte nel progetto:
   *  `app/layout.tsx` non dichiara nessun `metadataBase`, quindi anche
   *  il canonical degli editoriali esce relativo. Per questo l'unico
   *  posto da cui prenderla è il browser, al momento del clic (vedi
   *  `url` più sotto). Il giorno in cui il layout dichiara un
   *  `metadataBase`, quello diventa la fonte da preferire. */
  href: string;
  title: string;
  lead: string;
  /** Il testo integrale, già in un'unica stringa. */
  body: string;
};

/** L'adattatore per i dispacci, così le pagine dell'articolo non si
 *  scrivono a mano la stessa conversione in due punti. */
export function newsItemShareTarget(item: NewsItem): ShareTarget {
  return {
    id: item.id,
    href: articleHref(item),
    title: item.title,
    lead: item.lead,
    body: item.body,
  };
}

/** Il segno "copia" della variante a icone.
 *
 *  Nella barra estesa la stessa cosa è il carattere ⎘ (U+2398), che sta
 *  in pochissimi font: JetBrains Mono arriva qui in un subset latino che
 *  non lo contiene, quindi lo disegna il ripiego di sistema, e su parecchi
 *  sistemi non lo disegna nessuno. Lì accanto c'è la parola "COPIA" e il
 *  bottone si capisce lo stesso; qui non c'è, e un bottone con dentro un
 *  quadratino vuoto non vuol dire niente. Da qui il disegno, nella stessa
 *  forma dell'icona di condivisione: stessa scatola da 11 e stesso tratto. */
function SegnoCopia() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="text-accent"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Il foglio dietro, di cui si vedono solo il lato alto e quello
          sinistro: due rettangoli interi, a questa misura, si leggono
          come un reticolo invece che come due fogli. */}
      <path d="M8 3.6 V1.3 H1.3 V8 H3.6" />
      <rect x="3.6" y="3.6" width="6.1" height="6.1" rx="0.6" />
    </svg>
  );
}

type ShareBarProps = {
  target: ShareTarget;
  /** Solo le icone, senza le etichette: serve dove la barra sta in una
   *  colonna stretta, come il margine sinistro della pagina di lettura
   *  di un editoriale, dove i tre bottoni per esteso non ci starebbero
   *  su una riga sola. Il nome accessibile non cambia: è l'`aria-label`,
   *  che c'è in tutti e due i casi. */
  compact?: boolean;
  /** `rimandi` è la resa della pagina del dispaccio (disegni del
   *  15/10/2026): due comandi di testo, «Copia link» e «Condividi», in
   *  Geist nel mare con l'icona, sottolineati al passaggio. «Copia
   *  testo» lì non c'è: il disegno non lo porta, e sui riservati il
   *  testo è il solo sommario. */
  variante?: "targhe" | "rimandi" | "icone";
  className?: string;
};

/** Il bottone della variante `icone` (riga del caso di un riservato,
 *  tavola «Notizia riservata»): un quadrato da 34 col solo segno nel mare.
 *  Il nome sta nel `title` (che si legge al passaggio) e nell'`aria-label`.
 *  Esportato per i comandi dell'editoriale (ComandiLettura), che dal
 *  26/10/2026 hanno la stessa forma. */
export const COMANDO_ICONA =
  "grid size-[34px] shrink-0 cursor-pointer place-items-center rounded-[2px] border border-rule bg-transparent p-0 text-mare transition-colors hover:border-mare " +
  FUOCO;

/** Il comando di testo della variante `rimandi` (`.n-azioni` del disegno). */
export const COMANDO_RIMANDO =
  "inline-flex cursor-pointer items-center gap-1.5 border-0 border-b border-transparent bg-transparent px-0 py-1 font-sans text-corpo font-semibold text-mare transition-colors hover:border-current " +
  FUOCO;

export function ShareBar({ target, compact = false, variante = "targhe", className }: ShareBarProps) {
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const flash = (msg: string) => {
    setToast(msg);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 1800);
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      flash(label);
    } catch {
      flash(label);
    }
  };

  const url =
    typeof window !== "undefined"
      ? `${location.origin}${target.href}`
      : target.href;
  const plainText = `${target.title}\n\n${target.lead}\n\n${target.body}\n\n— Osservatorio Italiano · ${target.id}\n${url}`;

  const share = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({
          title: target.title,
          text: target.lead,
          url,
        });
        return;
      } catch {
        // user cancelled or share failed — fall back to copy
      }
    }
    await copy(url, "LINK COPIATO");
  };

  const avviso = toast ? (
    <div className="absolute right-0 top-[calc(100%+6px)] z-[5] whitespace-nowrap rounded-sm bg-ink px-2.5 py-1.5 text-[10px] tracking-comando text-surface shadow-[0_4px_14px_rgba(0,0,0,0.25)]">
      ✓ {toast}
    </div>
  ) : null;

  if (variante === "icone") {
    return (
      <div className={cn("relative flex shrink-0 items-center gap-2", className)}>
        <button
          type="button"
          className={COMANDO_ICONA}
          onClick={() => copy(url, "LINK COPIATO")}
          title="Copia link"
          aria-label="Copia il link"
        >
          <Icona nome="collegamento" className="size-[18px]" />
        </button>
        <button type="button" className={COMANDO_ICONA} onClick={share} title="Condividi" aria-label="Condividi il dispaccio">
          <Icona nome="condividi" className="size-[18px]" />
        </button>
        {avviso}
      </div>
    );
  }

  if (variante === "rimandi") {
    return (
      <div className={cn("relative flex flex-wrap items-center gap-x-[22px] gap-y-1.5", className)}>
        <button type="button" className={COMANDO_RIMANDO} onClick={() => copy(url, "LINK COPIATO")}>
          <Icona nome="collegamento" className="size-[14px]" />
          Copia link
        </button>
        <button type="button" className={COMANDO_RIMANDO} onClick={share}>
          <Icona nome="condividi" className="size-[14px]" />
          Condividi
        </button>
        {avviso}
      </div>
    );
  }

  const btn = cn(
    "inline-flex cursor-pointer items-center rounded-sm border border-rule bg-transparent text-[10px] tracking-comando text-ink",
    compact ? "h-[26px] w-[26px] justify-center" : "gap-1 px-2.5 py-1",
  );

  return (
    <div className={cn("relative flex items-center gap-1.5", className)}>
      <button
        type="button"
        className={btn}
        onClick={() => copy(url, "LINK COPIATO")}
        title="Copia link"
        aria-label="Copia il link"
      >
        <span className="text-accent">↗</span>
        {compact ? null : " LINK"}
      </button>
      <button
        type="button"
        className={btn}
        onClick={() => copy(plainText, "TESTO COPIATO")}
        title="Copia testo integrale"
        aria-label="Copia il testo integrale"
      >
        {compact ? <SegnoCopia /> : <span className="text-accent">⎘</span>}
        {compact ? null : " COPIA"}
      </button>
      <button
        type="button"
        className={btn}
        onClick={share}
        title="Condividi l'articolo"
        aria-label="Condividi"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 11 11"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
          className="text-accent"
        >
          <line x1="2.5" y1="5.5" x2="8.5" y2="2.5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
          <line x1="2.5" y1="5.5" x2="8.5" y2="8.5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
          <circle cx="2.5" cy="5.5" r="1.4" fill="currentColor" />
          <circle cx="8.5" cy="2.5" r="1.4" fill="currentColor" />
          <circle cx="8.5" cy="8.5" r="1.4" fill="currentColor" />
        </svg>
        {compact ? null : "CONDIVIDI"}
      </button>
      {avviso}
    </div>
  );
}
